// components/Atoms/Image/Image.js
import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import { resolveMediaUrl } from '../../../utils/resolveMediaUrl';

const Image = ({
  image,
  isThumbnail = false,
  className = '',
  fit = 'responsive',
  priority = true,
}) => {
  if (!image) return null;

  let src = '';
  let alt = 'Image';
  let width = 1200;
  let height = 800;
  let srcset;

  if (image.original) {
    const imageData = isThumbnail && image.thumbnail ? image.thumbnail : image.original;
    src = imageData.full_url || imageData.fullUrl || imageData.src || '';
    alt = imageData.alt || alt;
    width = imageData.width || width;
    height = imageData.height || height;
    srcset = image.srcset || imageData.srcset;
  } else if (typeof image === 'object') {
    src = image.full_url || image.fullUrl || image.url || image.src || '';
    alt = image.alt || alt;
    width = image.width || width;
    height = image.height || height;
    srcset = image.srcset;
  } else if (typeof image === 'string') {
    src = image;
  }

  src = resolveMediaUrl(src);
  if (!src) return null;

  const fitClass =
    {
      responsive: 'image-fit-contain',
      contain: 'image-fit-contain',
      cover: 'image-fit-cover',
      fill: 'image-fit-fill',
    }[fit] || 'image-fit-contain';

  // Dev <img> for easier debugging (no inline styles)
  if (process.env.NODE_ENV === 'development') {
    let srcSetString = '';
    if (srcset) {
      const entries = [];
      if (srcset.small?.src) entries.push(`${resolveMediaUrl(srcset.small.src)} 480w`);
      if (srcset.medium?.src) entries.push(`${resolveMediaUrl(srcset.medium.src)} 768w`);
      if (srcset.large?.src) entries.push(`${resolveMediaUrl(srcset.large.src)} 1024w`);
      if (srcset.extra_large?.src) entries.push(`${resolveMediaUrl(srcset.extra_large.src)} 1200w`);
      srcSetString = entries.join(', ');
    }

    return (
      <div className={`image ${fitClass} ${className}`}>
        <img
          className="image-img"
          src={src}
          alt={alt}
          srcSet={srcSetString || undefined}
          onError={(e) => e.currentTarget.classList.add('image-error')}
        />
      </div>
    );
  }

  if (fit === 'fill') {
    return (
      <div className={`image ${fitClass} ${className}`}>
        <NextImage
          src={src}
          alt={alt}
          fill
          sizes="100vw"
          className="image-img"
          priority={priority}
          unoptimized
        />
      </div>
    );
  }

  return (
    <div className={`image ${fitClass} ${className}`}>
      <NextImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes="(max-width: 480px) 100vw, (max-width: 768px) 85vw, (max-width: 1024px) 75vw, 50vw"
        className="image-img"
        priority={priority}
        unoptimized
      />
    </div>
  );
};

Image.propTypes = {
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      full_url: PropTypes.string,
      fullUrl: PropTypes.string,
      url: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      srcset: PropTypes.object,
    }),
    PropTypes.shape({
      original: PropTypes.shape({
        full_url: PropTypes.string,
        fullUrl: PropTypes.string,
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      thumbnail: PropTypes.shape({
        full_url: PropTypes.string,
        fullUrl: PropTypes.string,
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      srcset: PropTypes.object,
    }),
  ]).isRequired,
  isThumbnail: PropTypes.bool,
  className: PropTypes.string,
  fit: PropTypes.oneOf(['responsive', 'contain', 'cover', 'fill']),
  priority: PropTypes.bool,
};

export default Image;
